import React, { useCallback, memo, useEffect } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { Map, List, Twitter } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import IncidentList from './IncidentList';
import IncidentMap from './IncidentMap';
import ReportIncidentModal from './ReportIncidentModal';
import ReportButton from './ReportButton';
import AuthModal from './auth/AuthModal';
import Header from './Header';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { currentViewAtom, isAuthModalOpenAtom, isReportModalOpenAtom, isMenuOpenAtom } from '../atoms/ui';
import { incidentsAtom, isLoadingIncidentsAtom } from '../atoms/incidents';
import { userLocationAtom } from '../atoms/location';
import { userAtom, isLoadingAuthAtom } from '../atoms/auth';
import { logger } from '../lib/logger';
import { createIncident, fetchIncidents } from '../services/incidents';
import { toast } from './ui/use-toast';
import type { IncidentCreate } from '../types';
import { mapBoundsAtom } from '../atoms/map';

export default memo(function IncidentDashboard() {
  const [view, setView] = useAtom(currentViewAtom);
  const [isReportModalOpen, setIsReportModalOpen] = useAtom(isReportModalOpenAtom);
  const [isAuthModalOpen, setIsAuthModalOpen] = useAtom(isAuthModalOpenAtom);
  const [isMenuOpen, setIsMenuOpen] = useAtom(isMenuOpenAtom);
  const [incidents, setIncidents] = useAtom(incidentsAtom);
  const [mapBounds] = useAtom(mapBoundsAtom);
  const [isLoadingIncidents, setIsLoadingIncidents] = useAtom(isLoadingIncidentsAtom);
  const [userLocation] = useAtom(userLocationAtom);
  const [user] = useAtom(userAtom);
  const [isLoadingAuth] = useAtom(isLoadingAuthAtom);

  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Use React Query for fetching incidents
  const { data, isLoading, isError } = useQuery({
    queryKey: ['incidents', mapBounds],
    queryFn: async () => {
      if (!mapBounds) {
        logger.warn('No map bounds set, returning empty array.', 'IncidentDashboard');
        return [];
      }
      // Add pagination or limit parameter if fetchIncidents supports it
      return fetchIncidents(mapBounds, { limit: 50 }); // Increase limit if supported
    },
    enabled: !!mapBounds,
    staleTime: 30000,
    refetchInterval: 60000,
  });

  // Update atoms on data change
  useEffect(() => {
    setIsLoadingIncidents(isLoading);
    if (data && Array.isArray(data)) {
      logger.info('Setting incidents in the dashboard', 'IncidentDashboard', { count: data.length });
      setIncidents(data);
    }
  }, [isLoading, data, setIsLoadingIncidents, setIncidents]);

  const handleReportClick = useCallback(() => {
    logger.info('Report button clicked', 'user_interaction');
    if (isLoadingAuth) {
      return;
    }
    
    if (user) {
      setIsReportModalOpen(true);
    } else {
      setIsAuthModalOpen(true);
    }
  }, [user, isLoadingAuth, setIsReportModalOpen, setIsAuthModalOpen]);

  const handleAuthModalClose = useCallback(() => {
    setIsAuthModalOpen(false);
    if (user) {
      setIsReportModalOpen(true);
    }
  }, [user, setIsAuthModalOpen, setIsReportModalOpen]);

  const handleSubmitReport = useCallback(async (newIncident: IncidentCreate) => {
    try {
      await createIncident(newIncident);
      // Immediately invalidate the incidents query to show the new incident
      await queryClient.invalidateQueries({ queryKey: ['incidents'] });
      setIsReportModalOpen(false);
      
      toast({
        title: t('incident.report.success'),
        description: t('incident.report.successDetail'),
      });
    } catch (error) {
      logger.error(error, 'create_incident');
      toast({
        title: t('incident.report.error'),
        description: error instanceof Error ? error.message : t('incident.report.errorDetail'),
        variant: 'destructive',
      });
      throw error;
    }
  }, [queryClient, t, setIsReportModalOpen]);

  const handleViewChange = useCallback((newView: string) => {
    setView(newView as 'map' | 'list');
  }, [setView]);

  return (
    <main className="h-[100dvh] w-screen relative overflow-hidden pt-16">
      <Header />

      {/* Floating UI Elements - hidden when menu is open */}
      {!isMenuOpen && !isReportModalOpen && (
        <>
          <div className="fixed top-16 left-0 right-0 z-20 bg-gradient-to-b from-white/90 to-white/0 pt-4 pb-8 px-4">
            <div className="flex items-center justify-center max-w-screen-xl mx-auto">
              <Tabs value={view} onValueChange={handleViewChange}>
                <TabsList className="max-w-[200px]">
                  <TabsTrigger value="map" className="flex-1">
                    <Map className="w-4 h-4 mr-2" />Map
                  </TabsTrigger>
                  <TabsTrigger value="list" className="flex-1">
                    <List className="w-4 h-4 mr-2" />List
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>

          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-20">
            <ReportButton 
              onClick={handleReportClick}
              onAuthRequired={() => setIsAuthModalOpen(true)}
              isLoading={isLoadingAuth}
            />
          </div>

          <div className="fixed bottom-6 right-6 z-20">
            <a 
              href="https://x.com/HaitiNextHQ" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center w-10 h-10 bg-white rounded-full shadow-lg hover:shadow-xl transition-shadow duration-200 text-gray-600 hover:text-blue-400"
              aria-label="Follow us on X (Twitter)"
            >
              <Twitter className="h-5 w-5" />
            </a>
          </div>
        </>
      )}

      <div className="h-full w-full">
        {isLoadingIncidents ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          </div>
        ) : view === 'map' ? (
          <div className="h-full w-full">
            <IncidentMap />
          </div>
        ) : incidents.length === 0 ? (
          <div className="h-full flex items-center justify-center px-4">
            <div className="text-center">
              <p className="text-gray-500 mb-4">{t('incident.empty.message')}</p>
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={handleReportClick}
                  className="text-red-600 hover:text-red-700 font-medium"
                >
                  {t('incident.empty.reportButton')}
                </button>
                <a 
                  href="https://x.com/HaitiNextHQ" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-blue-400 transition-colors"
                >
                  <Twitter className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full overflow-auto pt-24 pb-24 px-4">
            <div className="max-w-2xl mx-auto">
              <IncidentList incidents={incidents} />
            </div>
          </div>
        )}
      </div>

      <ReportIncidentModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        onSubmit={handleSubmitReport}
      />

      <AuthModal />
    </main>
  );
});